import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Download, Search, Eye, Users, Phone, DollarSign, Calendar, Pencil, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Customers = () => {
  const { t } = useLanguage();
  const { token } = useAuth();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerDetails, setCustomerDetails] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Edit state
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', phone: '' });
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  // Delete state
  const [deletingCustomer, setDeletingCustomer] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deletingNow, setDeletingNow] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (customerId) => {
    try {
      const response = await axios.get(`${API_URL}/api/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerDetails(response.data);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching customer details:', error);
    }
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setEditForm({ name: customer.name || '', phone: customer.phone || '' });
    setIsEditOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingCustomer) return;
    if (!editForm.name.trim() || !editForm.phone.trim()) {
      toast.error('Ad və telefon boş ola bilməz');
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(
        `${API_URL}/api/customers/${editingCustomer.id}`,
        { name: editForm.name.trim(), phone: editForm.phone.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Müştəri uğurla yeniləndi');
      setIsEditOpen(false);
      setEditingCustomer(null);
      await fetchCustomers();
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error(error.response?.data?.detail || 'Yeniləmə zamanı xəta baş verdi');
    } finally {
      setSavingEdit(false);
    }
  };

  const openDeleteDialog = (customer) => {
    setDeletingCustomer(customer);
    setIsDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingCustomer) return;
    setDeletingNow(true);
    try {
      const res = await axios.delete(`${API_URL}/api/customers/${deletingCustomer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const resCount = res.data?.reservations_deleted || 0;
      toast.success(
        resCount > 0
          ? `Müştəri və ${resCount} rezervasiya silindi`
          : 'Müştəri silindi'
      );
      setIsDeleteOpen(false);
      setDeletingCustomer(null);
      await fetchCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error(error.response?.data?.detail || 'Silinmə zamanı xəta baş verdi');
    } finally {
      setDeletingNow(false);
    }
  };

  const exportToExcel = () => {
    const exportData = customers.map(c => ({
      'Müştəri': c.name,
      'Telefon': c.phone,
      'Rezervasiya Sayı': c.reservation_count,
      'Ümumi Xərc (AZN)': c.total_spent,
      'Beh (AZN)': c.total_deposit,
      'Qalıq (AZN)': c.remaining,
      'Son Rezervasiya': c.last_reservation
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Müştərilər');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, `musteriler_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const formatCurrency = (amount) => `AZN ${(amount || 0).toLocaleString('az-AZ', { minimumFractionDigits: 2 })}`;

  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phone?.includes(searchTerm)
  );

  // Calculate totals
  const totals = {
    customers: customers.length,
    spent: customers.reduce((sum, c) => sum + (c.total_spent || 0), 0),
    deposit: customers.reduce((sum, c) => sum + (c.total_deposit || 0), 0),
    remaining: customers.reduce((sum, c) => sum + (c.remaining || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2D5016]"></div>
      </div>
    );
  }

  return (
    <div data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A2415] font-['Cormorant_Garamond']">
            {t('customer_list')}
          </h1>
          <p className="text-[#4A5D43] text-sm mt-1">Bütün müştəriləriniz və rezervasiya keçmişləri</p>
        </div>
        <Button
          onClick={exportToExcel}
          className="bg-[#2D5016] hover:bg-[#1A2415]"
          data-testid="export-customers-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          {t('customer_export')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="stat-card border-l-4 border-l-[#2D5016]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#2D5016]/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-[#2D5016]" />
            </div>
            <div>
              <p className="text-[#4A5D43] text-sm">Ümumi Müştəri</p>
              <p className="text-xl font-semibold text-[#1A2415]">{totals.customers}</p>
            </div>
          </div>
        </div>
        <div className="stat-card border-l-4 border-l-[#D97706]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D97706]/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-[#D97706]" />
            </div>
            <div>
              <p className="text-[#4A5D43] text-sm">Ümumi Xərc</p>
              <p className="text-xl font-semibold text-[#D97706]">{formatCurrency(totals.spent)}</p>
            </div>
          </div>
        </div>
        <div className="stat-card border-l-4 border-l-blue-500">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-[#4A5D43] text-sm">Ümumi Beh</p>
              <p className="text-xl font-semibold text-blue-500">{formatCurrency(totals.deposit)}</p>
            </div>
          </div>
        </div>
        <div className="stat-card border-l-4 border-l-red-500">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <p className="text-[#4A5D43] text-sm">Qalıq</p>
              <p className="text-xl font-semibold text-red-500">{formatCurrency(totals.remaining)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white border border-[#E5E3DB] rounded-lg p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A5D43]" />
          <Input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Müştəri adı və ya telefon nömrəsi ilə axtarın..."
            className="pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E3DB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F9F8F6] border-b border-[#E5E3DB]">
                <th className="text-left p-4 text-sm font-medium text-[#4A5D43]">{t('customer_name')}</th>
                <th className="text-left p-4 text-sm font-medium text-[#4A5D43]">{t('customer_phone')}</th>
                <th className="text-center p-4 text-sm font-medium text-[#4A5D43]">{t('customer_reservations')}</th>
                <th className="text-right p-4 text-sm font-medium text-[#4A5D43]">{t('customer_total')}</th>
                <th className="text-right p-4 text-sm font-medium text-[#4A5D43]">{t('customer_deposit')}</th>
                <th className="text-right p-4 text-sm font-medium text-[#4A5D43]">{t('customer_remaining')}</th>
                <th className="text-center p-4 text-sm font-medium text-[#4A5D43]">{t('customer_last')}</th>
                <th className="text-center p-4 text-sm font-medium text-[#4A5D43]">Əməliyyatlar</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-[#4A5D43]">
                    Heç bir müştəri tapılmadı
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer) => (
                  <tr
                    key={customer.id}
                    className="border-b border-[#E5E3DB] last:border-b-0 hover:bg-[#F9F8F6] transition-colors"
                    data-testid={`customer-row-${customer.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#2D5016]/10 flex items-center justify-center">
                          <span className="text-sm font-medium text-[#2D5016]">
                            {customer.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium text-[#1A2415]">{customer.name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-[#4A5D43]">
                        <Phone className="w-4 h-4" />
                        {customer.phone}
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#2D5016]/10 text-[#2D5016] font-medium">
                        {customer.reservation_count}
                      </span>
                    </td>
                    <td className="p-4 text-right font-medium text-[#D97706]">
                      {formatCurrency(customer.total_spent)}
                    </td>
                    <td className="p-4 text-right font-medium text-blue-500">
                      {formatCurrency(customer.total_deposit)}
                    </td>
                    <td className="p-4 text-right font-medium text-red-500">
                      {formatCurrency(customer.remaining)}
                    </td>
                    <td className="p-4 text-center text-[#4A5D43]">
                      <div className="flex items-center justify-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {customer.last_reservation || '-'}
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchCustomerDetails(customer.id)}
                          data-testid={`view-customer-${customer.id}`}
                          title="Detallar"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(customer)}
                          data-testid={`edit-customer-${customer.id}`}
                          title="Redaktə"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(customer)}
                          data-testid={`delete-customer-${customer.id}`}
                          title="Sil"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Customer Details Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Müştəri Detalları</DialogTitle>
          </DialogHeader>

          {customerDetails && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="flex items-center gap-4 p-4 bg-[#F9F8F6] rounded-lg">
                <div className="w-16 h-16 rounded-full bg-[#2D5016] flex items-center justify-center">
                  <span className="text-2xl font-medium text-white">
                    {customerDetails.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#1A2415]">{customerDetails.name}</h3>
                  <p className="text-[#4A5D43] flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {customerDetails.phone}
                  </p>
                </div>
              </div>

              {/* Reservations */}
              <div>
                <h4 className="text-lg font-medium text-[#1A2415] mb-4">Rezervasiya Tarixçəsi</h4>

                {customerDetails.reservations?.length === 0 ? (
                  <p className="text-[#4A5D43] text-center py-8">Heç bir rezervasiya yoxdur</p>
                ) : (
                  <div className="space-y-3">
                    {customerDetails.reservations?.map((res) => (
                      <div
                        key={res.id}
                        className="p-4 bg-white border border-[#E5E3DB] rounded-lg"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                              res.payment_status === 'paid' ? 'badge-paid' :
                              res.payment_status === 'deposit' ? 'badge-deposit' : 'badge-unpaid'
                            }`}>
                              {res.payment_status === 'paid' ? t('reservation_paid') :
                               res.payment_status === 'deposit' ? t('reservation_deposit_paid') : t('reservation_unpaid')}
                            </span>
                          </div>
                          <div className="text-right">
                            <p className="text-[#D97706] font-medium">{formatCurrency(res.total_amount)}</p>
                            <p className="text-xs text-[#4A5D43]">Beh: {formatCurrency(res.deposit)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-[#4A5D43]">
                          <span>{res.check_in} - {res.check_out}</span>
                        </div>
                        {res.notes && (
                          <p className="text-sm text-[#4A5D43] mt-2 italic">{res.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Customer Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-md" data-testid="edit-customer-dialog">
          <DialogHeader>
            <DialogTitle>Müştərini Redaktə Et</DialogTitle>
            <DialogDescription>
              Müştəri məlumatlarını yeniləyin. Ad dəyişikliyi rezervasiyalarda da əks olunacaq.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Ad Soyad</Label>
              <Input
                id="edit-name"
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Müştərinin adı"
                data-testid="edit-customer-name-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Telefon</Label>
              <Input
                id="edit-phone"
                type="text"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="+994 XX XXX XX XX"
                data-testid="edit-customer-phone-input"
                required
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditOpen(false)}
                disabled={savingEdit}
              >
                Ləğv Et
              </Button>
              <Button
                type="submit"
                className="bg-[#2D5016] hover:bg-[#1A2415]"
                disabled={savingEdit}
                data-testid="save-customer-btn"
              >
                {savingEdit ? 'Yadda saxlanılır...' : 'Yadda Saxla'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent data-testid="delete-customer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Müştərini silmək istədiyinizdən əminsiniz?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deletingCustomer?.name}</strong> adlı müştəri silinəcək.
              {deletingCustomer?.reservation_count > 0 && (
                <span className="block mt-2 text-red-600">
                  Diqqət: Bu müştəriyə aid <strong>{deletingCustomer.reservation_count}</strong> rezervasiya da silinəcək. Bu əməliyyat geri qaytarıla bilməz.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingNow}>Ləğv Et</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deletingNow}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-customer-btn"
            >
              {deletingNow ? 'Silinir...' : 'Bəli, Sil'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Customers;
